import styled, { css, DefaultTheme } from 'styled-components';
import { DesignToken, getSpacingToken, getToken, SpacingToken, Stack, Typography } from '@optimus-web/core';
import { TreeSize } from './types';

const getHeight = ({ theme, $size }: { theme: DefaultTheme; $size: TreeSize }) => {
    const options: Record<TreeSize, DesignToken> = {
        medium: 'sizing500',
        large: 'sizing700',
    };

    return getToken(options[$size])({ theme });
};

const getPadding = ({ $size }: { $size: TreeSize }) => {
    const options: Record<TreeSize, SpacingToken> = {
        medium: '50',
        large: '0',
    };

    return getSpacingToken(options[$size]);
};

export const OptionCounter = styled(Typography)<{ description: boolean }>`
    ${({ description }) =>
        description &&
        css`
            align-self: flex-start;
            padding-top: ${getSpacingToken('25')}px;
        `}
`;

export const OptionWrapper = styled.li<{ $size: TreeSize; disabled?: boolean; isGroup?: boolean }>`
    display: flex;
    align-items: center;
    box-sizing: border-box;

    width: 100%;
    height: ${getHeight}px;
    padding-top: ${getPadding}px;
    padding-bottom: ${getPadding}px;
    padding-right: ${getSpacingToken('100')}px;
    border-radius: ${getToken('borderRadius100')}px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    position: relative;

    &[aria-selected='true'] {
        border-color: ${getToken('paletteBasicsWhite')};
        border-style: solid;
        border-width: ${getToken('borderWidth100')}px 0;
    }

    :has(button:hover:not([role='checkbox'])):hover,
    :has(button:active:not([role='checkbox'])):active {
        background-color: unset;
    }

    ${({ disabled, isGroup }) =>
        (isGroup || !disabled) &&
        css`
            :hover {
                background-color: ${getToken('backgroundInteractiveNeutralSubtleHover')};
            }

            :active {
                background-color: ${getToken('backgroundInteractiveNeutralSubtleActive')};
            }

            &[aria-selected='true'] {
                :hover {
                    background-color: ${getToken('backgroundInteractiveSecondaryHover')};
                }

                :active {
                    background-color: ${getToken('backgroundInteractiveSecondaryActive')};
                }

                &,
                :has(button:hover:not([role='checkbox'])):hover,
                :has(button:active:not([role='checkbox'])):active {
                    background-color: ${getToken('backgroundInteractiveSecondaryDefault')};
                }
            }
        `}

    :focus-visible {
        z-index: 1;
    }
`;

export const OptionContent = styled(Stack)<{ $offset: number }>(
    ({ $offset, theme }) => css`
        padding-left: ${$offset * getSpacingToken('500')({ theme })}px;
    `
);

export const OptionSlot = styled(Stack)`
    width: ${getToken('sizing400')}px;
    height: ${getToken('sizing400')}px;
`;

OptionSlot.defaultProps = {
    itemAlignment: 'center',
    alignment: 'center',
};

export const OptionsList = styled.ul<{ expanded?: boolean }>`
    ${({ expanded }) =>
        expanded
            ? css`
                  padding: 1px;
                  margin: -1px;
              `
            : css`
                  padding: 0;
                  margin: 0;
              `}
    list-style: none;
`;
